import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { welcomeAppsHref } from './PhoneAppsGate';
import { clearPendingContactIfMatches, stashPendingContact } from '../../lib/openContactReturn';
import { accountNeedsSetup } from '../../lib/accountSetup';
import { onboardingAPI } from '../../lib/api';

const settledExistingAccounts = new Set();

// A mature account is not in the 8-step setup. Mark it onboarded once,
// from the client, and do not send it through the walkthrough. A new
// account still in setup is left alone here.
export function settleExistingAccount(user, refreshUser) {
  if (!user?.id || user.onboarded || accountNeedsSetup(user)) return;
  if (settledExistingAccounts.has(user.id)) return;
  settledExistingAccounts.add(user.id);
  onboardingAPI.markOnboarded()
    .then(() => refreshUser?.())
    .catch(() => { settledExistingAccounts.delete(user.id); });
}

// Setup still owes a new account the walkthrough. The people check is one
// of those steps, so /discovery stays open. A mature account is not in
// setup, and this does not send them back.
export function setupRedirect(pathname, user) {
  if (!accountNeedsSetup(user)) return null;
  if (pathname === '/discovery') return null;
  return '/onboarding';
}

export function ProtectedRoute({ children }) {
  const { user, loading, refreshUser } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (user?.onboarded) clearPendingContactIfMatches(location.pathname);
    settleExistingAccount(user, refreshUser);
  }, [user, location.pathname, refreshUser]);
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-nav-blue border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) {
    stashPendingContact(location.pathname);
    return <Navigate to="/login" />;
  }
  const welcome = welcomeAppsHref(location.pathname, location.search, user);
  if (welcome) return <Navigate to={welcome} replace />;
  const bounce = setupRedirect(location.pathname, user);
  if (bounce) return <Navigate to={bounce} />;
  return children;
}
